import { useMemo } from "react";
import useOrganizationEvents from "../../hooks/useOrganizationEvents";
import useUserActivities from "../../hooks/useUserActivities";
import { startOfMonth, startOfWeek, addDays } from "date-fns";
import { EventFragment, ActivityFragment } from "../../__generated__/graphql";
import { DashboardProvider } from "../../contexts/DashboardContext";
import { useDashboard } from "../../contexts/DashboardContext/hooks/useDashboard";
import PlanningView from "../../components/PlanningView";
import PageLayout from "../../components/PageLayout";

function DashboardV2Content() {
  const {
    selectedUsers,
    setSelectedUsers,
    selectedProjects,
    setSelectedProjects,
  } = useDashboard();

  // Calculate interval for data fetching (6 weeks calendar view)
  const interval = useMemo(() => {
    const currentMonth = new Date();
    const firstDayOfMonth = startOfMonth(currentMonth);
    const firstDayOfCalendar = startOfWeek(firstDayOfMonth, {
      weekStartsOn: 1,
    });
    const lastDayOfCalendar = addDays(firstDayOfCalendar, 41);

    return {
      start: firstDayOfCalendar,
      end: lastDayOfCalendar,
    };
  }, []);

  const {
    data: eventsData,
    loading: eventsLoading,
    error: eventsError,
  } = useOrganizationEvents({
    afterDate: interval.start.toISOString(),
    beforeDate: interval.end.toISOString(),
  });

  const {
    data: activitiesData,
    loading: activitiesLoading,
    error: activitiesError,
  } = useUserActivities({
    afterDate: interval.start.toISOString(),
    beforeDate: interval.end.toISOString(),
  });

  const events = useMemo(() => {
    return (
      eventsData?.events?.filter(
        (event): event is EventFragment => event !== null
      ) ?? []
    );
  }, [eventsData]);

  const activities = useMemo(() => {
    return (
      activitiesData?.userActivities?.filter(
        (activity): activity is ActivityFragment => activity !== null
      ) ?? []
    );
  }, [activitiesData]);

  const loading = eventsLoading || activitiesLoading;
  const error = eventsError || activitiesError;

  return (
    <PlanningView
      events={events}
      activities={activities}
      loading={loading}
      error={error}
      showProjectFilter={true}
      defaultDate={new Date()}
      selectedUsers={selectedUsers}
      onSelectedUsersChange={setSelectedUsers}
      selectedProjects={selectedProjects}
      onSelectedProjectsChange={setSelectedProjects}
    />
  );
}

export default function Dashboard() {
  return (
    <DashboardProvider>
      <PageLayout title="Planning" dataTestId="dashboard-page">
        <DashboardV2Content />
      </PageLayout>
    </DashboardProvider>
  );
}
